import React, { useState, useEffect } from 'react';
import {
  Server,
  Link2,
  Globe,
  ExternalLink,
  Plus,
  ArrowRight,
  Trash2,
  Loader2,
  Info,
  FileJson,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { toast as sonnerToast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RouteItem } from './route-item';
import { MigrateMountDialog } from './migrate-mount-dialog';
import { buildInstanceCdnUrl } from '@/lib/utils/cdn';
import { useLocation } from 'react-router-dom';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

// Quick matcher patterns
const QUICK_PATTERNS = {
  ALL: '.*',
  STEP1: '/step1',
  STEP2: '/step2',
  CHECKOUT: '/checkout',
  THANKYOU: '/thankyou',
} as const;

interface MountPointCardProps {
  mountPoint: any;
  id: string;
  availableInstances: any[];
}

/**
 * Mount Point Card Component
 *
 * Displays a single mount point with all its routes
 */
export function MountPointCard({ mountPoint, id, availableInstances }: MountPointCardProps) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [editMatcher, setEditMatcher] = useState('');
  const [editExcluder, setEditExcluder] = useState('');
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [newRouteMatcher, setNewRouteMatcher] = useState('');
  const [newRouteExcluder, setNewRouteExcluder] = useState('');
  const { storeRoutes, navigate: appNavigate, id: storeId } = useStoreNavigate();
  const location = useLocation();
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [instanceToRemove, setInstanceToRemove] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [instanceToPromote, setInstanceToPromote] = useState<string | null>(null);
  const [newConfigName, setNewConfigName] = useState('');

  // Animate progress bar when loading
  useEffect(() => {
    if (!loadingAction) {
      setLoadingProgress(0);
      return;
    }

    setLoadingProgress(10);
    const intervals = [
      setTimeout(() => setLoadingProgress(30), 200),
      setTimeout(() => setLoadingProgress(50), 400),
      setTimeout(() => setLoadingProgress(70), 800),
      setTimeout(() => setLoadingProgress(85), 1200),
    ];

    return () => intervals.forEach(clearTimeout);
  }, [loadingAction]);

  // Swap mutation (atomic unmount + mount)
  const swapMutation = trpc.plugins.swap.useMutation({
    onMutate: () => {
      setLoadingAction('Switching instance...');
    },
    onSuccess: () => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: 'Instance swapped successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  // Update route mutation
  const updateRouteMutation = trpc.plugins.updateRoute.useMutation({
    onMutate: () => {
      setLoadingAction('Updating route...');
    },
    onSuccess: () => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      setEditingRouteId(null);
      toast({
        title: 'Success',
        description: 'Route updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  // Mount mutation for adding new routes
  const mountMutation = trpc.plugins.mount.useMutation({
    onMutate: () => {
      setLoadingAction('Adding route...');
    },
    onSuccess: () => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      setShowAddRoute(false);
      setSelectedInstance(null);
      setNewRouteMatcher('');
      setNewRouteExcluder('');
      toast({
        title: 'Success',
        description: 'Route added successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  // Instantiate mutation for forking configs
  const forkForEditMutation = trpc.plugins.forkForEdit.useMutation({
    onError: (error: any) => {
      console.error('[forkForEdit] Mutation error:', error);
      toast({
        title: 'Fork Failed',
        description: error.message || 'Failed to create config fork',
        variant: 'destructive',
      });
    },
  });

  const instantiateMutation = trpc.plugins.instantiate.useMutation({
    onSuccess: () => {
      utils.plugins.list.invalidate();
      utils.plugins.listMountPoints.invalidate();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Remove instance mutation
  const removeInstanceMutation = trpc.plugins.removeInstance.useMutation({
    onMutate: () => {
      setLoadingAction('Removing instance...');
    },
    onSuccess: () => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: 'Instance removed successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  // Promote preview to production mutation
  const promotePreviewMutation = trpc.plugins.promotePreview.useMutation({
    onMutate: () => {
      setLoadingAction('Committing to production...');
    },
    onSuccess: (result) => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      setInstanceToPromote(null);
      setNewConfigName('');
      toast({
        title: 'Success',
        description: `Preview saved as "${result.configName}"`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  // Unmount mutation for deleting routes
  const unmountMutation = trpc.plugins.unmount.useMutation({
    onMutate: () => {
      setLoadingAction('Deleting route...');
    },
    onSuccess: () => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: 'Route deleted successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  const configureSplitMutation = trpc.plugins.configureSplit.useMutation({
    onMutate: () => {
      setLoadingAction('Configuring split testing...');
    },
    onSuccess: () => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: 'Split configuration saved',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  const reindexMutation = trpc.plugins.reindexSplitRoutes.useMutation({
    onMutate: () => {
      setLoadingAction('Reindexing split routes...');
    },
    onSuccess: (data: any) => {
      setLoadingProgress(100);
      utils.plugins.listMountPoints.invalidate();
      toast({
        title: 'Success',
        description: data.message || 'Routes reindexed successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setTimeout(() => setLoadingAction(null), 300);
    },
  });

  // Check if any mutation is loading
  const isAnyLoading =
    swapMutation.isPending ||
    updateRouteMutation.isPending ||
    mountMutation.isPending ||
    unmountMutation.isPending ||
    configureSplitMutation.isPending ||
    reindexMutation.isPending ||
    forkForEditMutation.isPending ||
    instantiateMutation.isPending ||
    removeInstanceMutation.isPending ||
    promotePreviewMutation.isPending;

  const getRouteIcon = () => {
    if (mountPoint.customDomain) return <Globe className="h-4 w-4 text-purple-500" />;
    if (mountPoint.routeType === 'alias') return <Link2 className="h-4 w-4 text-blue-500" />;
    return <Server className="h-4 w-4 text-gray-500" />;
  };

  const getRouteTypeLabel = () => {
    if (mountPoint.routeType === 'custom-domain') return 'Custom Domain';
    if (mountPoint.routeType === 'alias') return 'Alias';
    if (mountPoint.routeType === 'hostname') return 'Direct Instance';
    return 'Direct Instance';
  };

  const isPermanentCDN = mountPoint.routeType === 'hostname' && mountPoint.hostname?.includes('.cdn.');

  const formatUrl = () => {
    const protocol = mountPoint.hostname?.includes('localhost') ? 'http' : 'https';
    return `${protocol}://${mountPoint.hostname}${mountPoint.basePath === '/' ? '/' : mountPoint.basePath}`;
  };

  const currentRoutes = mountPoint.routes || [];

  // Build version/config matrix from ALL available instances (passed from parent)
  // Filter out preview instances from the matrix (they should not be in the switch UI)
  const allInstances: any[] = [];
  availableInstances.forEach((instance: any) => {
    // Skip preview instances in the matrix
    if (instance.isPreview) {
      return;
    }

    allInstances.push({
      instanceId: instance.id,
      version: instance.version,
      configName: instance.config?.configName || 'default',
      deploymentId: instance.deploymentId,
    });
  });

  const versionSet = new Set<string>();
  allInstances.forEach((i) => versionSet.add(String(i.version)));
  const versions = Array.from(versionSet).sort((a, b) =>
    b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }),
  );

  const configSet = new Set<string>();
  allInstances.forEach((i) => {
    const configName = String(i.configName);
    if (configName && configName !== 'undefined') {
      configSet.add(configName);
    }
  });
  const configs = Array.from(configSet).sort();

  // Build matrix: version -> config -> instances[]
  const matrix: Record<string, Record<string, any[]>> = {};
  versions.forEach((version) => {
    matrix[version] = {};
    configs.forEach((config) => {
      matrix[version][config] = [];
    });
  });

  allInstances.forEach((instance) => {
    const version = String(instance.version);
    const config = String(instance.configName);
    if (matrix[version] && matrix[version][config]) {
      matrix[version][config].push(instance);
    }
  });

  // Flatten matrix to arrays
  versions.forEach((version) => {
    configs.forEach((config) => {
      const instances = allInstances.filter((i) => i.version === version && i.configName === config);
      matrix[version][config] = instances.length > 0 ? instances : [];
    });
  });

  const handleSwitch = async (currentRoute: any, targetInstance: any) => {
    if (!targetInstance || !currentRoute) return;

    // Can't switch split routes - they don't have a single instance
    if (currentRoute.splitConfig) {
      toast({
        title: 'Cannot Switch',
        description: 'Split routes cannot be switched. Use the Split button to reconfigure.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Determine mount type and prepare parameters
      const isAlias = mountPoint.routeType === 'alias';
      const isCustomDomain = mountPoint.customDomain;

      // Extract alias from hostname if it's an alias mount
      // e.g., "lolo--store_xxx.cdn.localhost:3000" -> "lolo"
      const alias = isAlias ? mountPoint.hostname?.split('--')[0] : undefined;

      const swapParams: any = {
        currentDeploymentId: currentRoute.instance?.deploymentId,
        currentDeploymentInstanceId: currentRoute.instanceId,
        targetDeploymentId: targetInstance.deploymentId,
        targetDeploymentInstanceId: targetInstance.instanceId,
        storeId: id,
        basePath: mountPoint.basePath,
        matcher: currentRoute.matcher,
        excluder: currentRoute.excluder,
      };

      // Add the appropriate mount target
      if (isAlias && alias) {
        swapParams.alias = alias;
      } else if (isCustomDomain) {
        swapParams.customDomain = mountPoint.hostname;
      } else {
        swapParams.hostname = mountPoint.hostname;
      }

      // Swap (atomic unmount + mount)
      await swapMutation.mutateAsync(swapParams);
    } catch (error: any) {
      // Error handling is done in the mutation callbacks
      console.error('Switch failed:', error);
    }
  };

  const handleEditRoute = (route: any) => {
    setEditingRouteId(route.routeId);
    setEditMatcher(route.matcher || '');
    setEditExcluder(route.excluder || '');
  };

  const handleSaveEdit = async (route: any) => {
    try {
      const isAlias = mountPoint.routeType === 'alias';
      const isCustomDomain = mountPoint.customDomain;
      const alias = isAlias ? mountPoint.hostname?.split('--')[0] : undefined;

      const updateParams: any = {
        deploymentId: route.instance?.deploymentId,
        storeId: id,
        basePath: mountPoint.basePath,
        matcher: editMatcher,
        excluder: editExcluder || undefined,
      };

      // For split routes, pass routeId; for single instance routes, pass deploymentInstanceId
      if (route.splitConfig) {
        updateParams.routeId = route.routeId;
      } else {
        updateParams.deploymentInstanceId = route.instanceId;
      }

      if (isAlias && alias) {
        updateParams.alias = alias;
      } else if (isCustomDomain) {
        updateParams.customDomain = mountPoint.hostname;
      } else {
        updateParams.hostname = mountPoint.hostname;
      }

      await updateRouteMutation.mutateAsync(updateParams);
    } catch (error: any) {
      console.error('Update route failed:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingRouteId(null);
    setEditMatcher('');
    setEditExcluder('');
  };

  const handleConfigureSplit = async (route: any, splitConfig: any) => {
    try {
      await configureSplitMutation.mutateAsync({
        routeId: route.routeId || route.id,
        storeId: id,
        splitConfig,
      });
    } catch (error: any) {
      console.error('[handleConfigureSplit] Error:', error);
    }
  };

  const handleDeleteRoute = async (route: any) => {
    if (!confirm('Are you sure you want to delete this route?')) {
      return;
    }

    try {
      // For split routes, use direct route removal (unmount by routeId)
      if (route.splitConfig) {
        const unmountParams = {
          instanceId: route.splitConfig.instances[0].instanceId, // Just need any instance from the split for security check
          storeId: id,
          routeId: route.routeId,
        };

        console.log('[DELETE ROUTE] Unmounting split route:', unmountParams);
        await unmountMutation.mutateAsync(unmountParams);
        return;
      }

      // For single instance routes
      const unmountParams: any = {
        instanceId: route.instanceId,
        storeId: id,
        routeId: route.routeId,
      };

      console.log('[DELETE ROUTE] Unmount params:', {
        ...unmountParams,
        mountPoint: {
          hostname: mountPoint.hostname,
          basePath: mountPoint.basePath,
          routeType: mountPoint.routeType,
        },
      });

      await unmountMutation.mutateAsync(unmountParams);
    } catch (error: any) {
      console.error('Delete route failed:', error);
    }
  };

  const handleDeleteMountPoint = async () => {
    try {
      const isAlias = mountPoint.routeType === 'alias';
      const isCustomDomain = mountPoint.customDomain;

      // For alias, extract just the alias part (before --)
      let alias: string | undefined;
      if (isAlias && mountPoint.hostname) {
        const match = mountPoint.hostname.match(/^([^-]+)--/);
        alias = match ? match[1] : mountPoint.hostname.split('--')[0];
      }

      console.log('[DELETE MOUNT POINT] Deleting all routes for:', {
        hostname: mountPoint.hostname,
        basePath: mountPoint.basePath,
        routeType: mountPoint.routeType,
        alias,
        routeCount: currentRoutes.length,
      });

      // Delete all routes in this mount point
      const deletePromises = currentRoutes.map(async (route: any) => {
        // For split routes, use routeId
        if (route.splitConfig) {
          const unmountParams = {
            instanceId: route.splitConfig.instances[0].instanceId,
            storeId: id,
            routeId: route.routeId,
          };
          console.log('[DELETE MOUNT POINT] Unmounting split route:', unmountParams);
          return unmountMutation.mutateAsync(unmountParams);
        }

        // For single instance routes
        const unmountParams: any = {
          instanceId: route.instanceId,
          storeId: id,
          routeId: route.routeId,
        };

        console.log('[DELETE MOUNT POINT] Unmounting route:', route.instanceId, unmountParams);
        return unmountMutation.mutateAsync(unmountParams as any);
      });

      await Promise.all(deletePromises);

      toast({
        title: 'Success',
        description: 'Mount point deleted successfully',
      });

      setShowDeleteConfirm(false);
    } catch (error: any) {
      console.error('Delete mount point failed:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete mount point',
        variant: 'destructive',
      });
    }
  };

  const handleMovePriority = async (route: any, direction: 'up' | 'down') => {
    try {
      const isAlias = mountPoint.routeType === 'alias';
      const isCustomDomain = mountPoint.customDomain;
      const alias = isAlias ? mountPoint.hostname?.split('--')[0] : undefined;

      // Sort routes the same way as in the render
      const sortedRoutes = [...currentRoutes].sort((a: any, b: any) => {
        // Sort by priority (lower number = higher priority, undefined = lowest priority)
        const aPriority = a.priority ?? 9999;
        const bPriority = b.priority ?? 9999;
        if (aPriority !== bPriority) return aPriority - bPriority;

        // Then by createdAt (newer routes get higher priority when priorities are equal)
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });

      console.log('[MOVE PRIORITY] Routes data:', {
        totalRoutes: currentRoutes.length,
        sortedRoutesLength: sortedRoutes.length,
        routeIdsInOrder: sortedRoutes.map((r: any) => ({ id: r.routeId, priority: r.priority })),
        targetRouteId: route.routeId,
      });

      const currentIndex = sortedRoutes.findIndex((r: any) => r.routeId === route.routeId);

      if (currentIndex === -1) {
        console.error('[MOVE PRIORITY] Route not found in sorted list');
        console.error('[MOVE PRIORITY] Looking for routeId:', route.routeId);
        console.error(
          '[MOVE PRIORITY] Available routeIds:',
          sortedRoutes.map((r: any) => r.routeId),
        );
        return;
      }

      // Determine target index
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      console.log('[MOVE PRIORITY] Index calculation:', {
        currentIndex,
        direction,
        targetIndex,
        sortedRoutesLength: sortedRoutes.length,
        wouldBeAtBoundary: targetIndex < 0 || targetIndex >= sortedRoutes.length,
      });

      if (targetIndex < 0 || targetIndex >= sortedRoutes.length) {
        console.warn('[MOVE PRIORITY] Cannot move - already at boundary', {
          currentIndex,
          targetIndex,
          totalRoutes: sortedRoutes.length,
          direction,
        });
        return;
      }

      const currentRouteToMove = sortedRoutes[currentIndex];
      const targetRouteToSwap = sortedRoutes[targetIndex];

      // For routes with null/undefined priorities OR same priority value (like both 9999),
      // we need to materialize them as sequential numbers. Otherwise swapping doesn't work.
      const needsMaterialization =
        currentRouteToMove.priority === null ||
        currentRouteToMove.priority === undefined ||
        targetRouteToSwap.priority === null ||
        targetRouteToSwap.priority === undefined ||
        currentRouteToMove.priority === targetRouteToSwap.priority; // Same value = can't swap!

      let newCurrentPriority: number;
      let newTargetPriority: number;

      if (needsMaterialization) {
        // Materialize all routes with sequential priorities based on their position
        console.log(
          '[MOVE PRIORITY] Materializing priorities to sequential numbers (null/undefined/duplicate detected)',
        );

        // Current route gets target's index, target gets current's index
        newCurrentPriority = targetIndex;
        newTargetPriority = currentIndex;
      } else {
        // Both have real, different priorities - just swap them
        newCurrentPriority = targetRouteToSwap.priority!;
        newTargetPriority = currentRouteToMove.priority!;
      }

      console.log('[MOVE PRIORITY] Swapping priorities:', {
        direction,
        needsMaterialization,
        current: {
          routeId: currentRouteToMove.routeId,
          deploymentId: currentRouteToMove.instance?.deploymentId,
          instanceId: currentRouteToMove.instanceId,
          splitConfig: !!currentRouteToMove.splitConfig,
          oldPriority: currentRouteToMove.priority,
          newPriority: newCurrentPriority,
        },
        target: {
          routeId: targetRouteToSwap.routeId,
          deploymentId: targetRouteToSwap.instance?.deploymentId,
          instanceId: targetRouteToSwap.instanceId,
          splitConfig: !!targetRouteToSwap.splitConfig,
          oldPriority: targetRouteToSwap.priority,
          newPriority: newTargetPriority,
        },
      });

      const buildUpdateParams = (route: any, newPriority: number) => {
        const deploymentId = route.instance?.deploymentId || route.deploymentId;

        if (!deploymentId) {
          console.error('[buildUpdateParams] Missing deploymentId for route:', {
            routeId: route.routeId,
            route,
          });
          throw new Error('Missing deploymentId for route update');
        }

        const params: any = {
          deploymentId,
          storeId: id,
          basePath: mountPoint.basePath,
          matcher: route.matcher,
          excluder: route.excluder,
          priority: newPriority,
        };

        // For split routes, pass routeId; for single instance routes, pass deploymentInstanceId
        if (route.splitConfig) {
          params.routeId = route.routeId;
        } else {
          params.deploymentInstanceId = route.instanceId;
        }

        if (isAlias && alias) {
          params.alias = alias;
        } else if (isCustomDomain) {
          params.customDomain = mountPoint.hostname;
        } else {
          params.hostname = mountPoint.hostname;
        }

        console.log('[buildUpdateParams] Built params:', params);
        return params;
      };

      // Perform atomic swap: both updates must succeed or both fail
      await Promise.all([
        updateRouteMutation.mutateAsync(buildUpdateParams(currentRouteToMove, newCurrentPriority)),
        updateRouteMutation.mutateAsync(buildUpdateParams(targetRouteToSwap, newTargetPriority)),
      ]);

      console.log('[MOVE PRIORITY] ✅ Priority swap completed successfully');

      toast({
        title: 'Success',
        description: `Route priority ${direction === 'up' ? 'increased' : 'decreased'}`,
      });
    } catch (error: any) {
      console.error('[MOVE PRIORITY] Failed:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update priority',
        variant: 'destructive',
      });
    }
  };

  const handleInstanceSelect = (instanceId: string) => {
    setSelectedInstance(instanceId);

    // Find the selected instance and its deployment
    const instance = availableInstances.find((i) => i.id === instanceId);
    if (instance?.metadata?.router) {
      // Pre-fill with default router config from manifest
      setNewRouteMatcher(instance.metadata.router.matcher || '.*');
      setNewRouteExcluder(instance.metadata.router.excluder || '');
    } else {
      // Fallback to catch-all
      setNewRouteMatcher('.*');
      setNewRouteExcluder('');
    }
  };

  const handleAddRoute = async () => {
    if (!selectedInstance || !newRouteMatcher) {
      toast({
        title: 'Error',
        description: 'Please select an instance and provide a matcher',
        variant: 'destructive',
      });
      return;
    }

    // Find the instance details
    const instance = allInstances.find((i) => i.instanceId === selectedInstance);
    if (!instance) return;

    try {
      const isAlias = mountPoint.routeType === 'alias';
      const isCustomDomain = mountPoint.customDomain;
      const alias = isAlias ? mountPoint.hostname?.split('--')[0] : undefined;

      // Calculate priority for the new route (assign next available priority)
      const existingRoutes = mountPoint.routes || [];
      const priorities = existingRoutes.map((r: any) => r.priority).filter((p: any) => typeof p === 'number');
      const maxPriority = priorities.length > 0 ? Math.max(...priorities) : -1;
      const newPriority = maxPriority + 1;

      console.log('[ADD ROUTE] Calculating priority:', {
        routesCount: existingRoutes.length,
        existingPriorities: priorities,
        maxPriority,
        newPriority,
      });

      const mountParams: any = {
        deploymentId: instance.deploymentId,
        deploymentInstanceId: instance.instanceId,
        storeId: id,
        basePath: mountPoint.basePath,
        matcher: newRouteMatcher,
        excluder: newRouteExcluder || undefined,
        priority: newPriority,
      };

      if (isAlias && alias) {
        mountParams.alias = alias;
      } else if (isCustomDomain) {
        mountParams.customDomain = mountPoint.hostname;
      } else {
        mountParams.hostname = mountPoint.hostname;
      }

      console.log('[ADD ROUTE] Calling mount mutation with params:', mountParams);
      await mountMutation.mutateAsync(mountParams);
      console.log('[ADD ROUTE] Mount mutation succeeded');
    } catch (error: any) {
      console.error('[ADD ROUTE] Mount mutation failed:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add route',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveInstance = async (instanceId: string) => {
    setInstanceToRemove(instanceId);
  };

  const confirmRemoveInstance = async () => {
    if (!instanceToRemove) return;

    try {
      await removeInstanceMutation.mutateAsync({
        instanceId: instanceToRemove,
        storeId: id,
      });
      setInstanceToRemove(null);
    } catch (error) {
      console.error('[confirmRemoveInstance] Error:', error);
    }
  };

  const handleCommitToProduction = (instanceId: string) => {
    // Find the instance to get its current config name
    const instance = availableInstances.find((i: any) => i.id === instanceId);
    if (instance) {
      // Generate default production name (remove -draft- suffix)
      const currentName = instance.config?.configName || 'default';
      const baseName = currentName.replace(/-draft-.*$/, '');
      setNewConfigName(baseName);
    }
    setInstanceToPromote(instanceId);
  };

  const confirmPromoteInstance = async () => {
    if (!instanceToPromote || !newConfigName.trim()) return;

    try {
      await promotePreviewMutation.mutateAsync({
        instanceId: instanceToPromote,
        newConfigName: newConfigName.trim(),
      });
    } catch (error) {
      console.error('[confirmPromoteInstance] Error:', error);
    }
  };

  const handleEditConfig = async (route: any) => {
    // Can't edit config for split routes directly
    if (route.splitConfig) {
      toast({
        title: 'Cannot Edit Config',
        description: "Split routes don't have a single config. Edit individual instances instead.",
        variant: 'destructive',
      });
      return;
    }
    const startTime = performance.now();
    console.log('⏱️ [handleEditConfig] START', {
      instanceId: route.instanceId,
      timestamp: new Date().toISOString(),
    });

    // Find the full instance data from availableInstances
    const findStart = performance.now();
    const fullInstance = availableInstances.find((i: any) => i.id === route.instanceId);
    console.log(`⏱️ [handleEditConfig] Find instance: ${(performance.now() - findStart).toFixed(0)}ms`);

    if (!fullInstance) {
      console.error('[handleEditConfig] Full instance not found for:', route.instanceId);
      toast({
        title: 'Error',
        description: 'Instance data not found',
        variant: 'destructive',
      });
      return;
    }

    // If already a preview instance, just open the editor (no fork needed)
    if (fullInstance.isPreview) {
      console.log('[handleEditConfig] Opening editor for existing preview:', fullInstance.id);
      console.log(`⏱️ [handleEditConfig] TOTAL (no fork): ${(performance.now() - findStart).toFixed(0)}ms`);

      // Navigate to editor page
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      appNavigate(`${storeRoutes.EDITOR(fullInstance.id)}?returnUrl=${returnUrl}`);
      return;
    }

    // For production instances, use optimized fork-for-edit mutation
    try {
      console.log('🔄 [handleEditConfig] Production instance detected, using optimized fork...');

      const forkStart = performance.now();
      const result = await forkForEditMutation.mutateAsync({
        sourceInstanceId: fullInstance.id,
        storeId: id,
      });
      console.log(`⏱️ [handleEditConfig] Fork operation: ${(performance.now() - forkStart).toFixed(0)}ms`);

      // Construct the forked instance data for the editor (no need to refresh!)
      const forkedInstance = {
        id: result.instanceId,
        deploymentId: fullInstance.deploymentId,
        pluginId: fullInstance.pluginId,
        version: fullInstance.version,
        config: {
          ...fullInstance.config,
          configName: result.configName,
        },
        storeId: id,
        isPreview: true,
      };

      console.log('[handleEditConfig] Opening editor for fork:', forkedInstance.id);
      console.log(`⏱️ [handleEditConfig] TOTAL: ${(performance.now() - startTime).toFixed(0)}ms`);

      // Navigate to editor page
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      appNavigate(`${storeRoutes.EDITOR(forkedInstance.id)}?returnUrl=${returnUrl}`);

      // Refresh in background (non-blocking)
      utils.plugins.listMountPoints.invalidate().catch((err) => {
        console.warn('[handleEditConfig] Background refresh failed:', err);
      });

      toast({
        title: 'Config Fork Created',
        description: `Editing ${result.configName} with live preview`,
      });
    } catch (error: any) {
      console.error('[handleEditConfig] Failed to fork instance:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create config fork',
        variant: 'destructive',
      });
    }
  };

  // Generate preview URL for the instance
  const getPreviewUrl = (instance: any) => {
    // Use the direct CDN hostname for this instance
    const instanceId = instance.id || instance.instanceId;
    return buildInstanceCdnUrl(instanceId);
  };

  // Copy mount point as JSON to clipboard
  const handleCopyMountPointJson = () => {
    const mountPointJson = JSON.stringify(mountPoint, null, 2);
    navigator.clipboard.writeText(mountPointJson);
    sonnerToast.success('Mount point config copied to clipboard');
  };

  return (
    <Card className="border-border relative overflow-visible p-0 shadow-md">
      <div className="flex">
        {/* Internet/Traffic Source Indicator - Left Side Bar */}
        <div className="border-border/40 from-muted/30 flex w-6 shrink-0 flex-col items-center gap-1.5 border-r bg-gradient-to-b to-transparent py-3">
          <Globe className="text-muted-foreground/50 h-3.5 w-3.5" />
          <div className="from-border/30 h-full w-px bg-gradient-to-b to-transparent" />
          <ArrowRight className="text-muted-foreground/40 h-3 w-3" />
        </div>

        {/* Main Content */}
        <div className="flex flex-1 flex-col">
          {/* Mount Point Header - Ultra Compact */}
          <div className="bg-muted/40 flex items-center justify-between border-b px-3 py-2">
            <div className="flex items-center gap-2">
              {getRouteIcon()}
              <a
                href={formatUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary group flex items-center gap-1.5 transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  <code className="font-mono text-xs font-semibold leading-none">{mountPoint.hostname}</code>
                  <code className="bg-primary/10 text-primary rounded px-1.5 py-0.5 font-mono text-xs font-semibold leading-none">
                    {mountPoint.basePath || '/'}
                  </code>
                </div>
                <ExternalLink className="h-2.5 w-2.5 opacity-50 transition-opacity group-hover:opacity-100" />
              </a>
              <Badge variant="outline" className="h-4 text-[10px] font-normal">
                {getRouteTypeLabel()}
              </Badge>
            </div>
            <div className="flex items-center gap-1.5">
              <Badge variant="secondary" className="h-4 text-[10px] font-medium">
                {currentRoutes.length} {currentRoutes.length !== 1 ? 'routes' : 'route'}
              </Badge>
              {currentRoutes.length > 1 && (
                <Badge
                  variant="outline"
                  className="h-4 gap-0.5 text-[9px] font-normal"
                  title="Routes are checked from top to bottom. Route #1 = highest priority (checked first)"
                >
                  <Info className="h-2.5 w-2.5" />
                  #1 = highest
                </Badge>
              )}
              {!isPermanentCDN && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyMountPointJson}
                    className="h-6 gap-1 px-2 text-[11px]"
                    title="Copy entire mount point config as JSON"
                  >
                    <FileJson className="h-3 w-3" />
                    Copy JSON
                  </Button>

                  <MigrateMountDialog
                    id={id}
                    mountPoints={[]}
                    defaultSourceHostname={mountPoint.hostname}
                    defaultSourceType={
                      mountPoint.routeType === 'custom-domain' ? 'customDomain' : mountPoint.routeType
                    }
                  >
                    <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[11px]">
                      <ArrowRight className="h-3 w-3" />
                      Migrate
                    </Button>
                  </MigrateMountDialog>

                  <Dialog open={showAddRoute} onOpenChange={setShowAddRoute}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="h-6 gap-1 px-2 text-[11px]">
                        <Plus className="h-3 w-3" />
                        Add Route
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                      <DialogHeader>
                        <DialogTitle>Add Route to {mountPoint.hostname}</DialogTitle>
                        <DialogDescription>
                          Add a new route with a different matcher to this mount point
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        {/* Instance Selection */}
                        <div className="space-y-2">
                          <Label>Route to</Label>
                          <div className="bg-muted/30 max-h-[60vh] overflow-y-auto rounded-md border p-3">
                            {(() => {
                              // Group instances by plugin → version → config
                              const pluginMap = new Map<string, Map<string, Map<string, any>>>();

                              availableInstances.forEach((inst: any) => {
                                if (!pluginMap.has(inst.pluginId)) {
                                  pluginMap.set(inst.pluginId, new Map());
                                }
                                const versionMap = pluginMap.get(inst.pluginId)!;

                                if (!versionMap.has(inst.version)) {
                                  versionMap.set(inst.version, new Map());
                                }
                                const configMap = versionMap.get(inst.version)!;

                                const configName = inst.config?.configName || 'default';
                                configMap.set(configName, inst);
                              });

                              return (
                                <Accordion type="multiple" className="w-full">
                                  {Array.from(pluginMap.entries()).map(([pluginId, versionMap]) => {
                                    // Count total instances for this plugin
                                    let instanceCount = 0;
                                    versionMap.forEach((configMap) => {
                                      instanceCount += configMap.size;
                                    });

                                    return (
                                      <AccordionItem key={pluginId} value={pluginId} className="border-b">
                                        <AccordionTrigger className="py-3 hover:no-underline">
                                          <div className="flex items-center gap-2 text-left">
                                            <span className="text-sm font-semibold">{pluginId}</span>
                                            <Badge variant="secondary" className="text-xs">
                                              {instanceCount}
                                            </Badge>
                                          </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="pb-3">
                                          <div className="space-y-3 pl-2">
                                            {Array.from(versionMap.entries())
                                              .sort(([a], [b]) =>
                                                b.localeCompare(a, undefined, { numeric: true }),
                                              )
                                              .map(([version, configMap]) => (
                                                <div key={version} className="space-y-1.5">
                                                  {/* Version Header with count */}
                                                  <div className="flex items-center gap-2">
                                                    <span className="text-muted-foreground text-xs font-medium">
                                                      v{version}
                                                    </span>
                                                    <span className="text-muted-foreground text-xs">
                                                      ({configMap.size} config
                                                      {configMap.size !== 1 ? 's' : ''})
                                                    </span>
                                                  </div>

                                                  {/* Configs */}
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {Array.from(configMap.entries())
                                                      .sort(([a], [b]) => a.localeCompare(b))
                                                      .map(([configName, instance]) => (
                                                        <Badge
                                                          key={instance.id}
                                                          variant={
                                                            selectedInstance === instance.id
                                                              ? 'default'
                                                              : 'outline'
                                                          }
                                                          className={`cursor-pointer transition-all ${
                                                            selectedInstance === instance.id
                                                              ? 'bg-primary'
                                                              : 'hover:bg-primary/20'
                                                          }`}
                                                          onClick={() => handleInstanceSelect(instance.id)}
                                                        >
                                                          {configName}
                                                        </Badge>
                                                      ))}
                                                  </div>
                                                </div>
                                              ))}
                                          </div>
                                        </AccordionContent>
                                      </AccordionItem>
                                    );
                                  })}
                                </Accordion>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Matcher */}
                        <div className="space-y-2">
                          <Label htmlFor="new-matcher">Matcher</Label>
                          <Input
                            id="new-matcher"
                            placeholder="e.g., /step2 or ^/checkout.*"
                            value={newRouteMatcher}
                            onChange={(e) => setNewRouteMatcher(e.target.value)}
                          />
                          <div className="flex flex-wrap gap-1">
                            {[
                              { label: 'All', value: QUICK_PATTERNS.ALL },
                              { label: 'Step1', value: QUICK_PATTERNS.STEP1 },
                              { label: 'Step2', value: QUICK_PATTERNS.STEP2 },
                              { label: 'Checkout', value: QUICK_PATTERNS.CHECKOUT },
                            ].map((quick) => (
                              <Badge
                                key={quick.value}
                                variant="outline"
                                className="hover:bg-primary hover:text-primary-foreground cursor-pointer text-xs"
                                onClick={() => setNewRouteMatcher(quick.value)}
                              >
                                {quick.label}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Excluder */}
                        <div className="space-y-2">
                          <Label htmlFor="new-excluder">Excluder (optional)</Label>
                          <Input
                            id="new-excluder"
                            placeholder="e.g., /admin"
                            value={newRouteExcluder}
                            onChange={(e) => setNewRouteExcluder(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowAddRoute(false)}>
                          Cancel
                        </Button>
                        <Button onClick={handleAddRoute} disabled={!selectedInstance || !newRouteMatcher}>
                          Add Route
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Delete Mount Point Button */}
                  <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive h-6 gap-1 px-2 text-[11px]"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete Mount Point
                    </Button>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Mount Point?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will delete all {currentRoutes.length} route
                          {currentRoutes.length !== 1 ? 's' : ''} for{' '}
                          <strong>
                            {mountPoint.hostname}
                            {mountPoint.basePath}
                          </strong>
                          .
                          <br />
                          <br />
                          This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteMountPoint}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete All Routes
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>

          {/* Routes List - Balanced spacing */}
          <div className="space-y-2.5 overflow-visible p-2.5">
            {currentRoutes
              .sort((a: any, b: any) => {
                // Sort by priority (lower number = higher priority, undefined = lowest priority)
                const aPriority = a.priority ?? 9999;
                const bPriority = b.priority ?? 9999;
                if (aPriority !== bPriority) return aPriority - bPriority;

                // Then by createdAt (newer routes get higher priority when priorities are equal)
                const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return bTime - aTime;
              })
              .map((route: any, index: number) => {
                // Enrich route with full instance data from availableInstances (for single instance routes)
                const fullInstance = route.instanceId
                  ? availableInstances.find((i: any) => i.id === route.instanceId)
                  : null;
                const enrichedRoute = {
                  ...route,
                  isPreview: fullInstance?.isPreview || false,
                  deploymentId: route.instance?.deploymentId,
                };

                return (
                  <RouteItem
                    key={route.routeId}
                    route={enrichedRoute}
                    isPermanentCDN={isPermanentCDN}
                    canDelete={!isPermanentCDN && currentRoutes.length > 1}
                    canRemoveInstance={fullInstance?.canRemove || false}
                    routeIndex={index + 1}
                    totalRoutes={currentRoutes.length}
                    canMoveUp={index > 0}
                    canMoveDown={index < currentRoutes.length - 1}
                    versions={versions}
                    configs={configs}
                    matrix={matrix}
                    availableInstances={availableInstances}
                    onEdit={handleEditRoute}
                    onSave={handleSaveEdit}
                    onCancel={handleCancelEdit}
                    onSwitch={handleSwitch}
                    onDelete={handleDeleteRoute}
                    onRemoveInstance={handleRemoveInstance}
                    onCommitToProduction={handleCommitToProduction}
                    onMovePriority={handleMovePriority}
                    onEditConfig={handleEditConfig}
                    onConfigureSplit={handleConfigureSplit}
                    isEditing={editingRouteId === route.routeId}
                    editMatcher={editMatcher}
                    editExcluder={editExcluder}
                    setEditMatcher={setEditMatcher}
                    setEditExcluder={setEditExcluder}
                  />
                );
              })}
          </div>
        </div>
      </div>

      {/* Remove Instance Confirmation Dialog */}
      <AlertDialog open={!!instanceToRemove} onOpenChange={(open) => !open && setInstanceToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Instance?</AlertDialogTitle>
            <AlertDialogDescription>
              This instance only has a direct route (or no routes) and can be safely removed. If it has a
              direct CDN route, it will also be removed. This action cannot be undone.
              <br />
              <br />
              <code className="bg-muted rounded px-2 py-1 text-xs">{instanceToRemove}</code>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveInstance}
              className="bg-destructive hover:bg-destructive/90"
            >
              {removeInstanceMutation.isPending ? 'Removing...' : 'Remove Instance'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Preview As Production Dialog */}
      <Dialog open={!!instanceToPromote} onOpenChange={(open) => !open && setInstanceToPromote(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Preview as Production Config</DialogTitle>
            <DialogDescription>
              Promote this preview instance to a permanent production configuration. This will remove the 24h
              TTL and make it available in the switch matrix.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="configName">Production Config Name</Label>
              <Input
                id="configName"
                placeholder="e.g., blue, red, default"
                value={newConfigName}
                onChange={(e) => setNewConfigName(e.target.value)}
                autoFocus
              />
              <p className="text-muted-foreground text-xs">
                Choose a name for this production configuration. It will appear in the switch matrix.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInstanceToPromote(null)}>
              Cancel
            </Button>
            <Button
              onClick={confirmPromoteInstance}
              disabled={!newConfigName.trim() || promotePreviewMutation.isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              {promotePreviewMutation.isPending ? 'Committing...' : 'Commit to Production'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Professional Loading Overlay */}
      {isAnyLoading && (
        <div className="bg-background/80 absolute inset-0 z-50 flex items-center justify-center rounded-lg backdrop-blur-sm">
          <div className="bg-card flex w-80 flex-col items-center gap-4 rounded-lg border p-6 shadow-lg">
            <Loader2 className="text-primary h-8 w-8 animate-spin" />
            <div className="w-full space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{loadingAction || 'Processing...'}</span>
                <span className="text-muted-foreground text-xs">{loadingProgress}%</span>
              </div>
              <Progress value={loadingProgress} className="h-2" />
            </div>
            <p className="text-muted-foreground text-center text-xs">
              Please wait while we update your routes
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}
